import React, { useEffect, useState } from 'react';
import { Alert } from '@mui/joy';
import { directSupabase } from '../../lib/supabaseClient';
import { listDocumentShareLinks } from '../../modules/docs/services/documentShareLinks.service';
import { buildDocumentTransmissionVersionInfo } from '../../modules/docs/utils/transmissionInfo';
import { filterShareLinksForArtefact } from '../../modules/docs/utils/transmissionArtefact';
import { visualDesignerSurveysService } from '../../modules/docs/services/visualDesignerSurveys.service';

async function versionLinesFingerprint(versionId: string): Promise<string> {
  const { data, error } = await directSupabase
    .from('document_version_lines')
    .select('name, qty, unit_price, line_total, meta_json')
    .eq('version_id', versionId)
    .order('sort_order', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = (data || []).map((row: any) => {
    const meta = row?.meta_json && typeof row.meta_json === 'object' ? row.meta_json : {};
    const specs = meta?.specs && typeof meta.specs === 'object' ? meta.specs : {};
    return [
      String(row.name || ''),
      String(row.qty ?? ''),
      String(row.unit_price ?? ''),
      String(row.line_total ?? ''),
      String(specs.width_mm ?? ''),
      String(specs.height_mm ?? ''),
    ].join('|');
  });
  return rows.join('\n');
}

/** Exception-only: open survey working copy differs from latest customer transmission. */
export default function SurveyTransmissionMismatchAlert({
  documentId,
  artefact,
  workingVersionId,
  workingVersionNumber,
  isTransmitted,
  overrideUnlocked,
  firstWorkingVersionId,
  finalWorkingVersionId,
  finalSurveyId,
  finalReapprovalTransmissionVersionId,
  refreshKey,
}: {
  documentId: string | null | undefined;
  artefact: 'first' | 'final';
  workingVersionId: string | null;
  workingVersionNumber?: number | null;
  isTransmitted?: boolean;
  overrideUnlocked?: boolean;
  firstWorkingVersionId?: string | null;
  finalWorkingVersionId?: string | null;
  finalSurveyId?: string | null;
  finalReapprovalTransmissionVersionId?: string | null;
  refreshKey?: string | number;
}) {
  const [hasMismatch, setHasMismatch] = useState(false);
  const [transmittedAt, setTransmittedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const id = String(documentId || '').trim();
    const workingId = String(workingVersionId || '').trim();
    if (!id || !workingId) {
      setHasMismatch(false);
      setTransmittedAt(null);
      return;
    }

    (async () => {
      const links = await listDocumentShareLinks(id);
      if (cancelled) return;

      let versionRow: {
        cloned_from_version_id?: string | null;
        diverged_from_clone?: boolean | null;
        version_number?: number | null;
        is_transmitted?: boolean | null;
        override_unlocked?: boolean | null;
      } | null = null;

      const full = await directSupabase
        .from('document_versions')
        .select('id, cloned_from_version_id, diverged_from_clone, version_number, is_transmitted, override_unlocked')
        .eq('id', workingId)
        .maybeSingle();
      if (full.error) {
        const fallback = await directSupabase
          .from('document_versions')
          .select('id, version_number, is_transmitted, override_unlocked')
          .eq('id', workingId)
          .maybeSingle();
        versionRow = fallback.data;
      } else {
        versionRow = full.data;
      }
      if (cancelled) return;

      let pins = { transmissionVersionIds: [] as string[], shareLinkIds: [] as string[] };
      const finalId = String(finalSurveyId || '').trim();
      if (finalId) {
        try {
          pins = await visualDesignerSurveysService.listFinalReapprovalPins(finalId);
        } catch {
          // Classification falls back to working ids / tags.
        }
      }
      if (cancelled) return;

      const scoped = filterShareLinksForArtefact(links, artefact, {
        firstWorkingVersionId,
        finalWorkingVersionId,
        finalReapprovalTransmissionVersionId,
        finalTransmissionVersionIds: pins.transmissionVersionIds,
        finalShareLinkIds: pins.shareLinkIds,
      });
      const info = buildDocumentTransmissionVersionInfo({
        activeVersionId: workingId,
        activeVersionNumber: workingVersionNumber
          ?? (versionRow?.version_number != null ? Number(versionRow.version_number) : null),
        activeIsTransmitted: isTransmitted ?? !!versionRow?.is_transmitted,
        activeOverrideUnlocked: overrideUnlocked ?? !!versionRow?.override_unlocked,
        activeClonedFromVersionId: versionRow?.cloned_from_version_id
          ? String(versionRow.cloned_from_version_id)
          : null,
        activeDivergedFromClone: !!versionRow?.diverged_from_clone,
        shareLinks: scoped,
      });

      let mismatch = !!info.hasMismatch;
      // Unlock forks change the working id even when content is identical.
      if (mismatch && !versionRow?.diverged_from_clone) {
        const sentId = String(
          [...scoped]
            .filter((row) => !row.revokedAt)
            .sort((a, b) => Date.parse(String(b.sentAt || '')) - Date.parse(String(a.sentAt || '')))[0]
            ?.sentVersionId
          || '',
        ).trim();
        if (sentId) {
          try {
            const [workingFp, sentFp] = await Promise.all([
              versionLinesFingerprint(workingId),
              versionLinesFingerprint(sentId),
            ]);
            if (workingFp === sentFp) mismatch = false;
          } catch {
            // Keep ID-based result if fingerprinting fails.
          }
        }
      }

      if (cancelled) return;
      setHasMismatch(mismatch);
      setTransmittedAt(info.transmittedAt);
    })().catch(() => {
      if (!cancelled) {
        setHasMismatch(false);
        setTransmittedAt(null);
      }
    });

    return () => { cancelled = true; };
  }, [
    documentId,
    artefact,
    workingVersionId,
    workingVersionNumber,
    isTransmitted,
    overrideUnlocked,
    firstWorkingVersionId,
    finalWorkingVersionId,
    finalSurveyId,
    finalReapprovalTransmissionVersionId,
    refreshKey,
  ]);

  if (!hasMismatch) return null;

  return (
    <Alert color="warning" variant="soft" sx={{ width: '100%' }}>
      This {artefact === 'final' ? 'Final' : 'First'} Survey no longer matches the latest customer transmission
      {transmittedAt ? ` (last sent ${new Date(transmittedAt).toLocaleString('en-GB')})` : ''}.
      Unlock edits the working copy only — retransmit if the customer should see the update.
    </Alert>
  );
}
